import { Box, InputBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { tokens } from "../../../theme";
import { showToastMessageRequestError } from "../../../utils/toasts";
import { yupRequired } from "../../../utils/validation";
import SHModal from "../../theme/Modal/SHModal";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";

const MEcheckCheckSerialNumber = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);


    const [snObject, setSnObject] = useState({
        barcode: "",
    });

    const handleClose = (value = null) => {
        setSnObject({
            barcode: "",
        });

        props.handleClose(value);
    }

    // Hot-reload safety: parent's chosenProductForSerial gets nuked on HMR while
    // this modal stays open. Auto-close so we don't sit on a useless modal.
    useEffect(() => {
        if (props.open && !props.chosenProductForSerial) {
            handleClose(null);
        }
    }, [props.open, props.chosenProductForSerial]);


    const [fieldErrors, setFieldErrors] = useState({});

    const rules = {
        barcode: yupRequired('barcode', t, false),
    };
    
    const schema = yup.object().shape(rules);

    // skuSerialMap is keyed by SKU or barcode. Build candidate keys from the SKU and the
    // discrete product.barcodes entries; splitting the joined product.barcode string breaks
    // when an individual SKU/barcode contains commas.
    const product = props.chosenProductForSerial?.product;
    const productBarcode = product?.barcode;
    const barcodeKeys = [
        product?.sku,
        ...(product?.barcodes?.map(b => b.barcode) || []),
        productBarcode,
        ...(productBarcode?.split(",").map(s => s.trim()) || []),
    ].filter(Boolean);
    const skuSerialEntries = barcodeKeys.reduce(
        (acc, key) => acc ?? props.skuSerialMap?.[key],
        undefined
    );
    const allowedBarcodes = skuSerialEntries?.map(sn => sn.barcode) || [];

    const SaveSerialNumber = async () => {
        try {

            setFieldErrors({});
            await schema.validate(snObject, { abortEarly: false });

            const matchedBarcode = allowedBarcodes.find(b =>
                b === snObject.barcode ||
                b?.split(",").map(s => s.trim()).includes(snObject.barcode)
            );

            if (!matchedBarcode) {
                setFieldErrors({
                    barcode: "This barcode is not allowed for this product."
                });
                return; // stop execution
            }

            handleClose(matchedBarcode);
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
              } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("update_failed"), t, err);
              }
        }
    };
    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleChange = (key, value) => {
        setSnObject(prev => ({
          ...prev,
          [key]: value
        }));
        setFieldErrors({
            ...fieldErrors,
            [key]: ''
          });
      };
      
    return(
        <SHModal
            open={props.open}
            onClose={() => handleClose(null)}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    alignItems:'center',
                    gap:1
                }}>
                <Subheading>{t("scan_one_of_serial_numbers")}</Subheading>
                <Text>
                    {skuSerialEntries?.map(sn => sn.barcode).join(", ")}
                </Text>
            </Box>
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    justifyContent:"space-between",
                    gap:2
                }}
            >
                <Box
                    sx={{
                        width:120,
                    }}
                >
                    <Text>{t("barcode")}</Text>
                </Box>
                
                <InputBase
                    onChange={(e) => {
                        handleChange("barcode",e.target.value)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            SaveSerialNumber();
                        }
                    }}
                    autoFocus
                    sx={{
                        flex:1,
                        backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                        borderRadius:4,
                        display:"flex",
                        height:32,
                        px:2,
                    }}
                />
            
            </Box>
            <Box
                component="footer"
                sx={{
                    
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={() => handleClose(null)} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton 
                    className={"group-btn"} 
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained" 
                    onClick={() => SaveSerialNumber()}
                    >{t("save")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MEcheckCheckSerialNumber;
